'use client'

import { useEffect, useState } from 'react'

import type { TocHeading } from '@/lib/mdx'
import { cn } from '@/lib/utils'

export type ArticleTocProps = {
  headings: TocHeading[]
}

/**
 * Scroll-spy table of contents built from a page's H2/H3 heading IDs. Real
 * `<a href="#id">` anchors — no `onClick` + `router.push`, so keyboard/tab
 * order and native anchor behavior work for free. Originally built for the
 * blog article page (`renderMdx`'s `TocHeading[]`, slugged by
 * `rehype-slug`); the methodology page reuses it with a hand-authored
 * `TocHeading[]` array since it isn't MDX-rendered.
 */
export function ArticleToc({ headings }: ArticleTocProps) {
  const [activeId, setActiveId] = useState<string | null>(headings[0]?.id ?? null)

  useEffect(() => {
    if (headings.length === 0) return

    const elements = headings
      .map((heading) => document.getElementById(heading.id))
      .filter((el): el is HTMLElement => el !== null)

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (visible.length === 0) return
        // Prefer the topmost heading still above the "active" line when
        // multiple headings intersect simultaneously.
        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        )
        setActiveId(topmost.target.id)
      },
      // Biased toward the top of the viewport so a heading counts "active"
      // once it's crossed just below the sticky header/rail offset.
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [headings])

  if (headings.length === 0) return null

  return (
    <nav aria-label="Table of contents" className="hidden lg:block">
      <p className="type-overline mb-3 text-muted-foreground">On this page</p>
      <ol className="space-y-1">
        {headings.map((heading) => {
          const isActive = heading.id === activeId
          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                aria-current={isActive ? 'location' : undefined}
                className={cn(
                  'block border-l-2 py-1 text-sm transition-colors duration-200',
                  heading.depth === 3 ? 'pl-6' : 'pl-3',
                  isActive
                    ? 'border-foreground font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {heading.text}
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
